import type { WizardAccordionProps } from './components/accordion';
import { accordion, collapsible } from './components/accordion';
import type { WizardAnalysisProps } from './components/analysis';
import { analysis, analysisCompleteKey } from './components/analysis';
import type { WizardCanopyProps } from './components/canopy';
import { canopy, CANOPY_PASSWORD_TOKEN, CANOPY_THIRD_FIELD_TOKEN, CANOPY_USERNAME_TOKEN } from './components/canopy';
import type { UniclientCartProps } from './components/cart';
import type { UniclientCheckoutProps } from './components/checkout';
import type { WizardContentProps } from './components/content';
import { content, contentGrid, contentItem, contentSeparator } from './components/content';
import type { WizardDynamicListProps } from './components/dynamic_list';
import {
  DYNAMIC_LIST_CURRENT_ITERABLE_ATTRIBUTE,
  DYNAMIC_LIST_ITERABLE_ATTRIBUTE,
  dynamicList,
  dynamicListItemActions,
  FILE_UPLOAD_FILE_BINARY,
  FILE_UPLOAD_FILE_NAME,
  FILE_UPLOAD_FILE_TYPE,
  filesUpload,
  saveDynamicListValues,
} from './components/dynamic_list';
import type { FormProps, InlineFormProps } from './components/form/form';
import { column, form, inlineForm, inlineFormSkip, input, row } from './components/form/form';
import type { UniclientGridProps } from './components/grid';
import { grid, gridItem } from './components/grid';
import type { WizardIllustratedCardsProps } from './components/illustrated_cards';
import { illustratedCards } from './components/illustrated_cards';
import type { UniclientImage } from './components/image';
import { image } from './components/image';
import type { WizardInputCardsProps } from './components/input_cards';
import { inputCards } from './components/input_cards';
import type { UniclientListProps } from './components/list';
import { list, listItem, listSwitchItem } from './components/list';
import type { WizardOtpProps } from './components/otp';
import { otp, troubleshootByFixingPhoneNumber, troubleshootByResend, troubleshootBySkipping } from './components/otp';
import type { UniclientPageProps } from './components/page';
import type { WizardPhotoCollectionProps } from './components/photo_collection';
import { photoCollection } from './components/photo_collection';
import type { UniclientPriceProps } from './components/price';
import { price } from './components/price';
import type { UniclientPriceCardProps } from './components/price_card';
import { priceCard } from './components/price_card';
import type { UniclientProductCardProps } from './components/product_card';
import { productCard } from './components/product_card';
import { productSummary } from './components/product_summary';
import type { UniclientProductSummaryProps } from './components/product_summary';
import { progressCard } from './components/progress_card';
import type { WizardProgressCardProps } from './components/progress_card';
import { realtime } from './components/realtime';
import type { WizardRealtimeProps } from './components/realtime';
import { selectable } from './components/selectable';
import type { WizardSelectableProps } from './components/selectable';
import { selectableCards } from './components/selectable_cards';
import type { WizardSelectableCardsProps } from './components/selectable_cards';
import { specialFx } from './components/special_fx';
import type { UniclientSpecialFxProps } from './components/special_fx';
import type { UniclientStickerProps } from './components/sticker';
import { sticker } from './components/sticker';
import { tag } from './components/tag';
import { updatePayment } from './components/update_payment';
import type { WizardUpdatePaymentProps } from './components/update_payment';
import { paymentMethod } from './payment';

export type UniclientComponent<TComponentAttributes extends string, TUIAttributes extends string = string> =
  | FormProps<TComponentAttributes>
  | InlineFormProps<TComponentAttributes>
  | UniclientCartProps<TComponentAttributes>
  | UniclientCheckoutProps<TComponentAttributes>
  | UniclientGridProps
  | UniclientImage
  | UniclientListProps<TComponentAttributes>
  | UniclientPageProps<TComponentAttributes>
  | UniclientPriceCardProps
  | UniclientPriceProps
  | UniclientProductCardProps
  | UniclientProductSummaryProps
  | UniclientSpecialFxProps
  | UniclientStickerProps
  | WizardAccordionProps
  | WizardAnalysisProps
  | WizardCanopyProps<TComponentAttributes>
  | WizardContentProps
  | WizardDynamicListProps<TComponentAttributes, TUIAttributes>
  | WizardIllustratedCardsProps<TComponentAttributes>
  | WizardInputCardsProps
  | WizardOtpProps
  | WizardPhotoCollectionProps<TComponentAttributes>
  | WizardProgressCardProps
  | WizardRealtimeProps
  | WizardSelectableCardsProps<TComponentAttributes>
  | WizardSelectableProps<TComponentAttributes>
  | WizardUpdatePaymentProps<TComponentAttributes>;

export const FORM_COMPONENT = { ...input, row, column };

export const COMPONENTS = {
  image,
  price,
  priceCard,
  productSummary,
  sticker,
  accordion,
  collapsible,
  analysis: Object.assign(analysis, {
    completeKey: analysisCompleteKey,
  }),
  canopy: Object.assign(canopy, {
    usernameToken: CANOPY_USERNAME_TOKEN,
    passwordToken: CANOPY_PASSWORD_TOKEN,
    thirdFieldToken: CANOPY_THIRD_FIELD_TOKEN,
  } as const),
  tag,
  selectableCards,
  illustratedCards,
  updatePayment: Object.assign(updatePayment, { method: paymentMethod }),
  content: Object.assign(content, { grid: contentGrid, separator: contentSeparator, item: contentItem }),
  selectable,
  form,
  inlineForm: Object.assign(inlineForm, { skip: inlineFormSkip }),
  grid: Object.assign(grid, { item: gridItem }),
  list: Object.assign(list, { item: listItem, switchItem: listSwitchItem }),
  dynamicList: Object.assign(dynamicList, {
    saveValues: saveDynamicListValues,
    iterableAttribute: DYNAMIC_LIST_ITERABLE_ATTRIBUTE,
    currentIterableAttribute: DYNAMIC_LIST_CURRENT_ITERABLE_ATTRIBUTE,
    actions: dynamicListItemActions,
  }),
  filesUpload: Object.assign(filesUpload, {
    fileBinary: FILE_UPLOAD_FILE_BINARY,
    fileName: FILE_UPLOAD_FILE_NAME,
    fileType: FILE_UPLOAD_FILE_TYPE,
  }),
  otp: Object.assign(otp, {
    troubleshootByFixingPhoneNumber,
    troubleshootBySkipping,
    troubleshootByResend,
  }),
  specialFx,
  progressCard,
  realtime,
  productCard,
  inputCards,
  photoCollection,
};
